import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  CheckCircle2, XCircle, Clock, RefreshCw, FileText,
  ChevronLeft, ChevronRight, Inbox,
} from 'lucide-react';

import { glJournalApi }    from '@/api/glJournal.api';
import { usePermission }   from '@/hooks/usePermission';
import { Permissions }     from '@/constants/permissions';
import { Spinner }         from '@/components/ui/Spinner';
import { cn }              from '@/utils/cn';
import type { RootState }  from '@/state/store';

import type { GlJournalListDto, GlJournalFilterDto } from '@/types/glJournal.types';
import { WORKFLOW_STATUS_LABELS, WORKFLOW_STATUS_COLORS } from '@/types/glJournal.types';

// ── GL Approval Inbox ──────────────────────────────────────────────────────────
// Shows journals awaiting the current user's approval action (Submitted /
// PendingApproval status). Approvers click through to the journal form where the
// Approve / Reject buttons are shown contextually.

export const GlApprovalInboxPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();
  const canApprove        = hasPermission(Permissions.GL_JOURNAL_APPROVE);
  const canReject         = hasPermission(Permissions.GL_JOURNAL_REJECT);

  // Session context — forwarded to the API filter so the backend enforces scope
  const session = useSelector((s: RootState) => s.session.session);

  const [items,   setItems]   = useState<GlJournalListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  // Show both Submitted and PendingApproval journals via two calls merged.
  // Session context (FY, location, cost center) is passed to each call so the
  // backend enforces the same scope it would on the journal list endpoint.
  const load = useCallback(async (p: number) => {
    setLoading(true);
    try {
      // FY is intentionally omitted — approvers should see all pending journals
      // regardless of year. Backend enforces scope for restricted users.
      const base: GlJournalFilterDto = {
        pageNumber:    p,
        pageSize,
        sortBy:        'documentDate',
        sortDescending: true,
        locationId:    session?.locationId   ?? undefined,
        costCenterId:  session?.costCenterId ?? undefined,
      };

      // Fetch Submitted
      const [r1, r2] = await Promise.all([
        glJournalApi.getList({ ...base, workflowStatus: 'Submitted' }),
        glJournalApi.getList({ ...base, workflowStatus: 'PendingApproval' }),
      ]);

      const d1 = r1.data?.data;
      const d2 = r2.data?.data;

      // Merge and sort by document date descending
      const merged = [...(d1?.items ?? []), ...(d2?.items ?? [])].sort(
        (a, b) => new Date(b.documentDate).getTime() - new Date(a.documentDate).getTime()
      );

      setItems(merged);
      setTotal((d1?.totalCount ?? 0) + (d2?.totalCount ?? 0));
    } catch (err) {
      console.error('Failed to load approval inbox', err);
    } finally {
      setLoading(false);
    }
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(page); }, [page, load]);

  const totalPages = Math.ceil(total / pageSize);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* ── Header ────────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Inbox size={20} className="text-blue-600 dark:text-blue-400" />
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                Approval Inbox
              </h1>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total > 0
                ? `${total} journal${total !== 1 ? 's' : ''} awaiting your approval`
                : 'No journals currently awaiting approval'}
            </p>
          </div>
          <button
            onClick={() => load(page)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {/* Permission notice */}
        {!canApprove && !canReject && (
          <div className="mt-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400">
            You don't currently have permission to approve or reject journals.
            Contact your administrator if this is incorrect.
          </div>
        )}
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <CheckCircle2 size={40} className="mb-3 opacity-30 text-green-500" />
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              All clear — no journals awaiting approval
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
              Journals submitted for approval will appear here.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Document #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Narration</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Currency</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Submitted By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(j => (
                <tr
                  key={j.id}
                  className="hover:bg-blue-50 dark:hover:bg-slate-700/50 transition-colors"
                >
                  <td
                    className="px-4 py-3 font-mono font-semibold text-blue-700 dark:text-blue-400 cursor-pointer hover:underline"
                    onClick={() => navigate(`/gl/journals/${j.id}`)}
                  >
                    {j.documentNumber}
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {new Date(j.documentDate).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded text-xs font-mono">
                      {j.transactionTypeCode}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 max-w-xs truncate">
                    {j.narration ?? j.reference ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white font-semibold">
                    {j.totalDebit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 font-mono text-xs">
                    {j.currencyCode}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <Clock size={13} className="text-blue-500 shrink-0" />
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        WORKFLOW_STATUS_COLORS[j.workflowStatus],
                      )}>
                        {WORKFLOW_STATUS_LABELS[j.workflowStatus]}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                    {j.createdByName ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => navigate(`/gl/journals/${j.id}`)}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                   bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300
                                   hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                        title="Open journal to review and approve"
                      >
                        <FileText size={11} /> Review
                      </button>
                      {canApprove && (
                        <button
                          onClick={() => navigate(`/gl/journals/${j.id}`)}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                     bg-green-600 text-white hover:bg-green-700 transition-colors"
                          title="Open to approve"
                        >
                          <CheckCircle2 size={11} /> Approve
                        </button>
                      )}
                      {canReject && (
                        <button
                          onClick={() => navigate(`/gl/journals/${j.id}`)}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg
                                     bg-red-600 text-white hover:bg-red-700 transition-colors"
                          title="Open to reject"
                        >
                          <XCircle size={11} /> Reject
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ───────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">
            Page {page} of {totalPages} · {total.toLocaleString()} records
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const n = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
              return (
                <button
                  key={n}
                  onClick={() => handlePageChange(n)}
                  className={cn(
                    'w-8 h-8 text-sm rounded transition-colors',
                    n === page
                      ? 'bg-blue-600 text-white font-semibold'
                      : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'
                  )}
                >
                  {n}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
